import React, { useEffect, useState } from "react";
import { UserPlus, Trash2, KeyRound, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ROLE_OPTS = [
  { key: "kumendan", label: "Kumendan Pasukan" },
  { key: "manager", label: "Manager" },
  { key: "staff", label: "Staff" },
  { key: "cs", label: "CS / Design" },
  { key: "qc", label: "QC" },
  { key: "produksi", label: "Produksi" },
];

export const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", role: "staff", name: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data.users);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`Akun ${form.username} dibuat`);
      setForm({ username: "", password: "", role: "staff", name: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat akun");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus akun ${u.username}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Akun dihapus");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus akun");
    }
  };

  const resetPass = async (u) => {
    const np = window.prompt(`Password baru untuk ${u.username} (min 6 karakter):`);
    if (!np) return;
    try {
      await api.post(`/users/${u.id}/reset-password`, { new_password: np });
      toast.success("Password akun diperbarui");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal reset password");
    }
  };

  return (
    <div className="space-y-6" data-testid="user-manager">
      <form onSubmit={create} className="glass rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3">
          <UserPlus className="w-5 h-5 text-blue-400" />
          <h3 className="font-display text-xl font-semibold">Buat Akun Baru</h3>
        </div>
        <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">Username</Label>
            <Input
              data-testid="new-username-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="username"
              className="mt-2 h-11 bg-slate-900/70 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">Password</Label>
            <Input
              data-testid="new-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="min 6 karakter"
              className="mt-2 h-11 bg-slate-900/70 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">Nama</Label>
            <Input
              data-testid="new-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="nama petugas"
              className="mt-2 h-11 bg-slate-900/70 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">Login Sebagai</Label>
            <select
              data-testid="new-role-select"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-2 h-11 w-full rounded-xl bg-slate-900/70 border border-white/10 px-4 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            >
              {ROLE_OPTS.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>
        <Button
          type="submit"
          data-testid="create-user-btn"
          disabled={busy}
          className="mt-7 h-12 rounded-xl bg-blue-600 hover:bg-blue-500 px-8 font-semibold lift"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" /> Simpan Akun</>}
        </Button>
      </form>

      <div className="glass rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-blue-400" />
          <h3 className="font-display text-xl font-semibold">
            Daftar Akun <span className="text-slate-500 text-sm font-normal">({users.length})</span>
          </h3>
        </div>
        <div className="mt-6 divide-y divide-white/5" data-testid="user-list">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.username}`} className="py-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-slate-200 font-medium">
                  {u.username}
                  {u.is_seed && (
                    <span className="ml-2 rounded-md border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-300">
                      utama
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {ROLE_OPTS.find((r) => r.key === u.role)?.label} · {u.name || "-"}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid={`reset-pass-btn-${u.username}`}
                  onClick={() => resetPass(u)}
                  variant="outline"
                  className="h-9 rounded-lg border-white/10 bg-slate-800/60 hover:bg-blue-600/20 text-xs"
                >
                  <KeyRound className="w-4 h-4 mr-1.5" /> Reset Password
                </Button>
                {!u.is_seed && (
                  <Button
                    data-testid={`delete-user-btn-${u.username}`}
                    onClick={() => remove(u)}
                    variant="outline"
                    className="h-9 w-9 p-0 rounded-lg border-red-400/20 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-7 rounded-xl border border-white/10 bg-slate-900/60 p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Info Login</div>
          <p className="mt-2 text-sm text-slate-400">
            Semua akun (Manager, Staff, QC, Produksi, Kumendan) masuk memakai username & password
            yang dibuat di halaman ini. Login PIN sudah tidak digunakan.
          </p>
        </div>
      </div>
    </div>
  );
};
